import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  FolderOpen,
  MessageSquare,
  Calendar,
  CalendarDays,
  Bell,
  Building2,
  Users,
  Settings,
  Shield,
  UsersRound,
  BarChart3,
  Bug,
  Lightbulb,
  Headphones,
  HardHat,
  NotebookText,
  AlertTriangle,
  Clock,
  ClipboardCheck,
  Package,
  HandCoins,
  Coins,
  DraftingCompass,
  FileQuestionMark,
  Tractor,
  ShieldAlert,
  Orbit,
  Box,
  Landmark,
  Receipt,
  Wallet,
  IdCard,
  ShieldCheck,
  Sparkles,
  Calculator,
  Megaphone,
  Home,
  FileSignature,
  GraduationCap,
  CircleDollarSign,
  LineChart,
  ChevronDown,
  Scale,
  User,
  Layers,
  Presentation,
  Settings2,
  FileCheck,
  FileKey,
  PackageCheck,
  GitBranch,
  Eye,
  Palmtree,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useProject } from '../../hooks/useProject';
import { useMenuNotifications } from '../../hooks/useMenuNotifications';
import { hasPermission } from '../../lib/permissions';
import { ProjectSelector } from './ProjectSelector';

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  adminOnly?: boolean;
  /** admin + yonetici (backend'in izin verdiği yönetim ekranları; route guard ile hizalı). */
  managerOnly?: boolean;
  permission?: { module: string; action: string };
  /** Sabit rol kısıtı (backend sabit rol kontrolü yapan modüller için, örn. AI asistanı). */
  fixedRoles?: string[];
  badgeKey?: string;
  /** Birleşik öğelerde birden fazla menü-bildirim key'i toplanır (örn. Resmi Yazışma: transmittal+submittal). */
  badgeKeys?: string[];
  /** Birleşik öğelerde alt modüllerden HERHANGİ birinin izni yeterli (OR). `permission` ile birlikte kullanılmaz. */
  anyPermissions?: { module: string; action: string }[];
  /** Aktif-route eşleşmesinde `to` yanında değerlendirilecek ek path önekleri (örn. detay route'ları). */
  matchPrefixes?: string[];
  /** Tanımlıysa aktif vurgu için location.search bu parçayı da içermelidir (örn. 'tab=zaman-cizelgesi'). */
  matchSearch?: string;
  disabled?: boolean;
  originalTo?: string;
}

interface NavGroup {
  id: string;
  label: string;
  icon: React.ElementType;
  items: NavItem[];
}

/** G1 — Ana Alan: grupsuz, üstte sabit. */
const mainItems: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: { module: 'dashboard', action: 'view' } },
  { to: '/tasks', label: 'Görevler', icon: ClipboardList, badgeKey: 'tasks', permission: { module: 'tasks', action: 'view' } },
  { to: '/messages', label: 'Mesajlaşma', icon: MessageSquare, badgeKey: 'messages', permission: { module: 'messages', action: 'view' } },
  { to: '/calendar', label: 'Takvim', icon: Calendar, badgeKey: 'calendar', permission: { module: 'calendar', action: 'view' } },
  { to: '/agenda', label: 'Ajanda', icon: CalendarDays, badgeKey: 'agenda', permission: { module: 'agenda', action: 'view' } },
  { to: '/files', label: 'Dosyalar', icon: FolderOpen, badgeKey: 'files', permission: { module: 'files', action: 'view' } },
];

/** G2–G9 — accordion grupları (NAVIGASYON_YENIDEN_YAPILANMA_PLANI.md §1). */
const navGroups: NavGroup[] = [
  {
    id: 'saha-operasyonu',
    label: 'Saha Operasyonu',
    icon: HardHat,
    items: [
      { to: '/daily-reports', label: 'Şantiye Defteri', icon: NotebookText, permission: { module: 'site_diary', action: 'view' } },
      { to: '/site-timeline', label: 'Şantiye Zaman Çizelgesi', icon: HardHat, badgeKey: 'site_timeline' },
      { to: '/punch-list', label: 'Kusur / Eksik Listesi', icon: AlertTriangle, permission: { module: 'punch_list', action: 'view' } },
      { to: '/attendance', label: 'Puantaj', icon: Clock, permission: { module: 'attendance', action: 'view' } },
      { to: '/leaves', label: 'İzinler', icon: Palmtree, badgeKey: 'leaves', permission: { module: 'leaves', action: 'view' } },
      { to: '/meetings', label: 'Toplantılar', icon: Presentation, permission: { module: 'meetings', action: 'view' } },
      { to: '/inspections', label: 'Denetimler', icon: ClipboardCheck, permission: { module: 'inspections', action: 'view' } },
      { to: '/concrete', label: 'Beton Kalite', icon: Layers, permission: { module: 'concrete', action: 'view' } },
      { to: '/schedule', label: 'İş Programı', icon: GitBranch, permission: { module: 'tasks', action: 'view' } },
    ],
  },
  {
    id: 'isg-uyum',
    label: 'İSG & Uyum',
    icon: ShieldAlert,
    items: [
      { to: '/safety', label: 'İSG', icon: ShieldAlert, permission: { module: 'safety', action: 'view' } },
      { to: '/work-permits', label: 'İş İzinleri', icon: FileKey, permission: { module: 'permits', action: 'view' } },
      { to: '/bbs-observations', label: 'BBS Gözlemleri', icon: Eye, permission: { module: 'safety', action: 'view' } },
      { to: '/skills', label: 'Beceri Matrisi', icon: GraduationCap, permission: { module: 'skills', action: 'view' } },
      { to: '/compliance', label: 'Uyum Dosyası (6331)', icon: FileCheck, permission: { module: 'compliance', action: 'view' } },
      { to: '/santiye-m', label: 'Şantiye-M', icon: HardHat, permission: { module: 'site_diary', action: 'view' } },
      { to: '/toolbox-talks', label: 'Toolbox Talk', icon: Megaphone, permission: { module: 'toolbox_talks', action: 'view' } },
      { to: '/worker-documents', label: 'Personel Belgeleri', icon: IdCard, permission: { module: 'worker_documents', action: 'view' } },
      { to: '/orientations', label: 'Oryantasyon', icon: GraduationCap, badgeKey: 'orientations', permission: { module: 'orientations', action: 'view' } },
      { to: '/instructions', label: 'İş Talimatları', icon: FileSignature, badgeKey: 'instructions', permission: { module: 'instructions', action: 'view' } },
    ],
  },
  {
    id: 'finans-satis',
    label: 'Finans & Satış',
    icon: Wallet,
    items: [
      // M2: Taşeronlar + İş Artış/Eksiliş + Fiyat Endeksleri + Kamu Hakediş Süreleri → Taşeron & Hakediş
      {
        to: '/subcontract-hub',
        label: 'Taşeron & Hakediş',
        icon: HandCoins,
        anyPermissions: [
          { module: 'subcontractors', action: 'view' },
          { module: 'change_orders', action: 'view' },
        ],
        matchPrefixes: ['/subcontractors', '/change-orders', '/price-indices', '/public-billing-deadlines', '/subcontracts'],
      },
      { to: '/checks', label: 'Çek / Senet', icon: Landmark, permission: { module: 'checks', action: 'view' } },
      { to: '/expenses', label: 'Masraflar', icon: Receipt, permission: { module: 'expenses', action: 'view' } },
      { to: '/cash', label: 'Şahsi Kasa', icon: Coins, badgeKey: 'cash', permission: { module: 'cash', action: 'view' } },
      { to: '/guarantees', label: 'Teminat Mektupları', icon: ShieldCheck, permission: { module: 'guarantees', action: 'view' } },
      { to: '/delivery', label: 'Teslim Belgeleri', icon: PackageCheck, permission: { module: 'guarantees', action: 'view' } },
      { to: '/cash-flow', label: 'Nakit Akışı', icon: Wallet, permission: { module: 'checks', action: 'view' } },
      { to: '/mali-takvim', label: 'Mali Takvim', icon: CalendarDays, permission: { module: 'dashboard', action: 'view' } },
      { to: '/mali-haberler', label: 'Mali Haberler', icon: Megaphone, permission: { module: 'dashboard', action: 'view' } },
      { to: '/units', label: 'Daire Satış', icon: Home, permission: { module: 'unit_sales', action: 'view' } },
      { to: '/sales-leads', label: 'Satış CRM', icon: Users, badgeKey: 'sales_leads', permission: { module: 'unit_sales', action: 'view' } },
      // A4: ERP hesap eşleştirmeleri — backend'de admin/yonetici veya dinamik rolde erp.view
      { to: '/erp-mappings', label: 'ERP Eşleştirme', icon: Settings2, permission: { module: 'erp', action: 'view' } },
    ],
  },
  {
    id: 'teknik-ofis',
    label: 'Teknik Ofis',
    icon: DraftingCompass,
    items: [
      { to: '/drawings', label: 'Çizimler', icon: DraftingCompass, permission: { module: 'drawings', action: 'view' } },
      { to: '/bim', label: 'BIM', icon: Box, permission: { module: 'bim', action: 'view' } },
      { to: '/tours', label: '360° Tur', icon: Orbit, permission: { module: 'tours', action: 'view' } },
      { to: '/estimates', label: 'Keşif & Poz', icon: Calculator, permission: { module: 'estimates', action: 'view' }, matchPrefixes: ['/estimates'] },
      // M1: RFI + Transmittal + Submittal → Resmi Yazışma (backend'de rfi key'i yok; rozet transmittal+submittal toplamı)
      {
        to: '/correspondence',
        label: 'Resmi Yazışma',
        icon: FileQuestionMark,
        anyPermissions: [
          { module: 'rfi', action: 'view' },
          { module: 'transmittals', action: 'view' },
          { module: 'submittals', action: 'view' },
        ],
        badgeKeys: ['transmittal', 'submittal'],
        matchPrefixes: ['/rfis', '/transmittals', '/submittals'],
      },
    ],
  },
  {
    id: 'tedarik-ekipman',
    label: 'Tedarik & Ekipman',
    icon: Package,
    items: [
      // M3: Stok + Malzeme Talepleri → Stok & Malzeme (rozet tek key: stock)
      {
        to: '/stock-hub',
        label: 'Stok & Malzeme',
        icon: Package,
        badgeKey: 'stock',
        anyPermissions: [
          { module: 'stock', action: 'view' },
          { module: 'material_requests', action: 'view' },
        ],
        matchPrefixes: ['/stock', '/material-requests'],
      },
      { to: '/equipment', label: 'Ekipman', icon: Tractor, permission: { module: 'equipment', action: 'view' } },
      { to: '/equipment-time', label: 'Makine Puantajı', icon: Clock, permission: { module: 'equipment', action: 'view' } },
      { to: '/bids', label: 'Teklifler', icon: Scale, permission: { module: 'bids', action: 'view' } },
    ],
  },
  {
    id: 'analiz-ai',
    label: 'Analiz & AI',
    icon: BarChart3,
    items: [
      { to: '/reports', label: 'Raporlar', icon: BarChart3, managerOnly: true },
      { to: '/copq', label: 'COPQ', icon: CircleDollarSign, permission: { module: 'copq', action: 'view' } },
      { to: '/evm', label: 'EVM', icon: LineChart, permission: { module: 'evm', action: 'view' } },
      { to: '/sgk-labor', label: 'SGK İşçilik', icon: Calculator, permission: { module: 'sgk', action: 'view' } },
      { to: '/ai-assistant', label: 'AI Asistan', icon: Sparkles, fixedRoles: ['admin', 'yonetici', 'sef'] },
    ],
  },
  {
    id: 'yonetim',
    label: 'Yönetim',
    icon: Shield,
    items: [
      { to: '/projects', label: 'Projeler', icon: Building2 },
      { to: '/users', label: 'Kullanıcılar', icon: Users, managerOnly: true },
      { to: '/user-groups', label: 'Kullanıcı Grupları', icon: UsersRound, managerOnly: true },
      { to: '/roles', label: 'Rol Yönetimi', icon: Shield, managerOnly: true },
      { to: '/bug-reports', label: 'Hata Bildirimleri', icon: Bug, managerOnly: true },
      { to: '/feature-requests', label: 'Özellik İstekleri', icon: Lightbulb, managerOnly: true },
    ],
  },
  {
    id: 'hesap',
    label: 'Hesap',
    icon: User,
    items: [
      { to: '/reminders', label: 'Hatırlatıcılarım', icon: Bell },
      { to: '/release-notes', label: 'Güncelleme Notları', icon: Megaphone },
      { to: '/support', label: 'Destek', icon: Headphones },
      { to: '/settings', label: 'Ayarlar', icon: Settings },
    ],
  },
];

const testIdMap: Record<string, string> = {
  '/dashboard': 'nav-dashboard',
  '/tasks': 'nav-tasks',
  '/files': 'nav-files',
  '/messages': 'nav-messages',
  '/calendar': 'nav-calendar',
  '/agenda': 'nav-agenda',
  '/daily-reports': 'nav-daily-reports',
  '/reminders': 'nav-reminders',
  '/release-notes': 'nav-release-notes',
  '/projects': 'nav-projects',
  '/site-timeline': 'nav-site-timeline',
  '/punch-list': 'nav-punch-list',
  '/attendance': 'nav-attendance',
  '/leaves': 'nav-leaves',
  '/meetings': 'nav-meetings',
  '/erp-mappings': 'nav-erp-mappings',
  '/inspections': 'nav-inspections',
  '/concrete': 'nav-concrete',
  '/stock-hub': 'nav-stock-hub',
  '/subcontract-hub': 'nav-subcontract-hub',
  '/drawings': 'nav-drawings',
  '/correspondence': 'nav-correspondence',
  '/instructions': 'nav-instructions',
  '/orientations': 'nav-orientations',
  '/equipment': 'nav-equipment',
  '/equipment-time': 'nav-equipment-time',
  '/bids': 'nav-bids',
  '/safety': 'nav-safety',
  '/tours': 'nav-tours',
  '/bim': 'nav-bim',
  '/checks': 'nav-checks',
  '/expenses': 'nav-expenses',
  '/cash': 'nav-cash',
  '/units': 'nav-units',
  '/cash-flow': 'nav-cash-flow',
  '/mali-takvim': 'nav-mali-takvim',
  '/mali-haberler': 'nav-mali-haberler',
  '/worker-documents': 'nav-worker-documents',
  '/guarantees': 'nav-guarantees',
  '/copq': 'nav-copq',
  '/evm': 'nav-evm',
  '/toolbox-talks': 'nav-toolbox-talks',
  '/sgk-labor': 'nav-sgk-labor',
  '/santiye-m': 'nav-santiye-m',
  '/compliance': 'nav-compliance',
  '/delivery': 'nav-delivery',
  '/schedule': 'nav-schedule',
  '/work-permits': 'nav-work-permits',
  '/skills': 'nav-skills',
  '/estimates': 'nav-estimates',
  '/bbs-observations': 'nav-bbs-observations',
  '/sales-leads': 'nav-sales-leads',
  '/ai-assistant': 'nav-ai-assistant',
  '/users': 'nav-users',
  '/user-groups': 'nav-user-groups',
  '/roles': 'nav-roles',
  '/reports': 'nav-reports',
  '/bug-reports': 'nav-bug-reports',
  '/feature-requests': 'nav-feature-requests',
  '/support': 'nav-support',
  '/settings': 'nav-settings',
};

const GROUPS_STORAGE_KEY = 'sidebar-groups';

function loadOpenGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(GROUPS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

export function Sidebar() {
  const { user } = useAuth();
  const { selectedProject } = useProject();
  const { getCount, markRead } = useMenuNotifications(user?.id);
  const location = useLocation();

  const isAdmin = user?.role === 'admin';

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(loadOpenGroups);

  /** Proje-bağımlı öğelerin dinamik hedeflerini çöz (mevcut davranışla aynı). */
  const resolveItem = (item: NavItem): NavItem => {
    if (item.to === '/site-timeline') {
      return {
        ...item,
        to: selectedProject ? '/daily-reports?tab=zaman-cizelgesi' : '/projects',
        originalTo: '/site-timeline',
        matchSearch: 'tab=zaman-cizelgesi',
        disabled: !selectedProject,
      };
    }
    if (item.to === '/punch-list') {
      return {
        ...item,
        to: selectedProject ? `/projects/${selectedProject.id}/punch-list` : '/projects',
        originalTo: '/punch-list',
        disabled: !selectedProject,
      };
    }
    if (item.to === '/attendance') {
      return {
        ...item,
        to: selectedProject ? `/projects/${selectedProject.id}/attendance` : '/projects',
        originalTo: '/attendance',
        disabled: !selectedProject,
      };
    }
    if (item.to === '/inspections') {
      return {
        ...item,
        to: selectedProject ? `/projects/${selectedProject.id}/inspections` : '/projects',
        originalTo: '/inspections',
        disabled: !selectedProject,
      };
    }
    return { ...item, originalTo: item.to };
  };

  const isVisible = (item: NavItem): boolean => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.managerOnly && !(user?.role === 'admin' || user?.role === 'yonetici')) return false;
    if (item.fixedRoles && !(user?.role && item.fixedRoles.includes(user.role))) return false;
    if (item.permission && !hasPermission(user, item.permission.module, item.permission.action)) return false;
    if (
      item.anyPermissions &&
      !item.anyPermissions.some((p) => hasPermission(user, p.module, p.action))
    ) {
      return false;
    }
    return true;
  };

  /** Öğenin aktif route ile eşleştiği en uzun önek (to + matchPrefixes); eşleşme yoksa null. */
  const activePrefix = (item: NavItem): string | null => {
    // Query'li hedefler (örn. hub sekmeleri) ancak search parçası da eşleşirse aktif sayılır.
    if (item.matchSearch && !location.search.includes(item.matchSearch)) return null;
    let best: string | null = null;
    for (const prefix of [item.to.split('?')[0], ...(item.matchPrefixes ?? [])]) {
      if (location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)) {
        if (!best || prefix.length > best.length) best = prefix;
      }
    }
    return best;
  };

  const visibleMainItems = mainItems.map(resolveItem).filter(isVisible);
  const visibleGroups = navGroups
    .map((group) => ({ ...group, items: group.items.map(resolveItem).filter(isVisible) }))
    .filter((group) => group.items.length > 0);

  // Aktif route'un bulunduğu grup: en uzun eşleşen önek kazanır (örn. /projects/3/punch-list,
  // /projects'ten önce eşleşir). Disabled öğeler aktif grup tespitine katılmaz.
  let activeGroupId: string | null = null;
  let bestScore = -1;
  for (const group of visibleGroups) {
    for (const item of group.items) {
      if (item.disabled) continue;
      const matched = activePrefix(item);
      if (matched) {
        const score = matched.length + (location.pathname === matched ? 1000 : 0);
        if (score > bestScore) {
          bestScore = score;
          activeGroupId = group.id;
        }
      }
    }
  }

  const isGroupOpen = (groupId: string): boolean =>
    groupId === activeGroupId ? true : (openGroups[groupId] ?? false);

  const toggleGroup = (groupId: string) => {
    setOpenGroups((prev) => {
      const currentlyOpen = groupId === activeGroupId ? true : (prev[groupId] ?? false);
      const next = { ...prev, [groupId]: !currentlyOpen };
      try {
        localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // localStorage dolu/kapalı olabilir; tercih kalıcı olmazsa da menü çalışır.
      }
      return next;
    });
  };

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon;
    const originalTo = item.originalTo || item.to;
    const testId = testIdMap[originalTo] || `nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`;

    if (item.disabled) {
      return (
        <span
          key={item.originalTo || item.to}
          data-testid={testId}
          aria-label={`${item.label} (önce proje seçin)`}
          title="Önce bir proje seçin"
          className="flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-400"
        >
          <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span className="flex-1">{item.label}</span>
        </span>
      );
    }

    const badgeKeys = item.badgeKeys ?? (item.badgeKey ? [item.badgeKey] : []);
    const badgeCount = badgeKeys.reduce((sum, key) => sum + getCount(key), 0);
    const badgeTestId =
      badgeKeys.length === 1 ? `menu-badge-${badgeKeys[0]}` : `menu-badge-${testId.replace(/^nav-/, '')}`;
    const isActive = activePrefix(item) !== null;

    return (
      <NavLink
        key={item.originalTo || item.to}
        to={item.to}
        data-testid={testId}
        aria-label={item.label}
        onClick={() => {
          for (const key of badgeKeys) markRead(key);
        }}
        className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
          isActive
            ? 'bg-blue-50 font-semibold text-blue-600'
            : 'text-slate-600 hover:bg-slate-100'
        }`}
      >
        <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="flex-1">{item.label}</span>
        {badgeCount > 0 && (
          <span
            data-testid={badgeTestId}
            className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
        {isActive && <span className="sr-only">(aktif sayfa)</span>}
      </NavLink>
    );
  };

  return (
    <aside data-testid="sidebar" className="flex h-screen w-[260px] flex-col border-r border-slate-200 bg-white lg:h-dvh">
      <div className="flex h-16 items-center gap-2 border-b border-slate-100 px-5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-sm font-bold text-white">
          R
        </div>
        <span className="text-lg font-bold text-slate-900">Rizmir</span>
      </div>

      <div className="px-4 py-3">
        <ProjectSelector />
      </div>

      <nav aria-label="Ana navigasyon" className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
        {/* G1 — Ana Alan (grupsuz) */}
        {visibleMainItems.map(renderNavItem)}

        {/* G2–G9 — accordion grupları */}
        {visibleGroups.map((group) => {
          const GroupIcon = group.icon;
          const isOpen = isGroupOpen(group.id);
          const groupBadgeCount = group.items.reduce(
            (sum, item) =>
              sum +
              (item.badgeKeys ?? (item.badgeKey ? [item.badgeKey] : [])).reduce(
                (s, key) => s + getCount(key),
                0,
              ),
            0,
          );

          return (
            <div key={group.id} className="pt-2">
              <button
                type="button"
                data-testid={`nav-group-${group.id}`}
                aria-expanded={isOpen}
                onClick={() => toggleGroup(group.id)}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
              >
                <GroupIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="flex-1 text-left">{group.label}</span>
                {!isOpen && groupBadgeCount > 0 && (
                  <span
                    data-testid={`nav-group-badge-${group.id}`}
                    aria-label={`${groupBadgeCount} okunmamış bildirim`}
                    className="h-2 w-2 shrink-0 rounded-full bg-red-500"
                  />
                )}
                <ChevronDown
                  className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? '' : '-rotate-90'}`}
                  aria-hidden="true"
                />
              </button>
              {isOpen && <div className="mt-1 space-y-1">{group.items.map(renderNavItem)}</div>}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
